import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MaskedInput } from '@/components/ui/masked-input';
import { Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export const EditPartnerModal = ({ partner, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: partner.name || '',
    company_name: partner.company_name || '',
    telefone: partner.telefone || '',
    email: partner.email || '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const validateTelefone = (tel) => {
    if (!tel) return true; // Empty is ok, will keep existing
    const digits = tel.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 11;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.telefone && !validateTelefone(formData.telefone)) {
      toast.error('Telefone inválido. Use o formato (XX) XXXXX-XXXX');
      return;
    }
    
    setLoading(true);

    try {
      const updateData = {};
      if (formData.name !== partner.name) updateData.name = formData.name;
      if (formData.company_name !== partner.company_name) updateData.company_name = formData.company_name;
      if (formData.telefone !== partner.telefone) updateData.telefone = formData.telefone;
      if (formData.email !== partner.email) updateData.email = formData.email;
      if (formData.password) updateData.password = formData.password;
      
      if (Object.keys(updateData).length === 0) {
        toast.info('Nenhuma alteração detectada');
        onClose();
        return;
      }
      
      await axios.put(`${API}/users/partners/${partner.id}`, updateData, getAuthHeaders());
      toast.success('Parceiro atualizado com sucesso!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar parceiro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="edit-partner-modal">
        <DialogHeader>
          <DialogTitle data-testid="edit-partner-modal-title">Editar Parceiro</DialogTitle>
          <DialogDescription>Atualize os dados do parceiro. Deixe a senha em branco para manter a atual.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="edit-partner-form">
          <div className="space-y-2">
            <Label htmlFor="name">Nome Completo</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Digite o nome completo"
              data-testid="edit-partner-name-input"
              className="input-focus"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company_name">Nome da Empresa</Label>
            <Input
              id="company_name"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              placeholder="Ex: Empresa XYZ ou Autônomo"
              data-testid="edit-partner-company-input"
              className="input-focus"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="telefone">Telefone/WhatsApp</Label>
            <MaskedInput
              id="telefone"
              mask="(99) 99999-9999"
              placeholder="(11) 99999-9999"
              value={formData.telefone}
              onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
              data-testid="edit-partner-telefone-input"
              className="input-focus"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@exemplo.com"
              data-testid="edit-partner-email-input"
              className="input-focus"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Nova Senha (opcional)</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Deixe em branco para manter a senha atual"
                data-testid="edit-partner-password-input"
                className="input-focus pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Preencha apenas se desejar redefinir a senha do parceiro
            </p>
          </div>
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 btn-transition"
              data-testid="cancel-edit-partner-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 btn-transition"
              data-testid="submit-edit-partner-button"
            >
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditPartnerModal;
